<template>
  <a-layout-content class="cv-page-content scroll-x">
    <div class="container h-100 cv-page-container">
      <a-row type="flex" justify="space-between" class="cv-page-header mb-4">
        <a-col v-if="viewedUserName" class="cv-page-title">
          <h2>
            {{
              cv
                ? cv.cv_alias
                  ? `CV ${cv.cv_alias} for ${viewedUserName}`
                  : `CV for ${viewedUserName}`
                : `CVs for ${viewedUserName}`
            }}
          </h2>
        </a-col>
        <a-col v-if="cv || isPreviewCv" class="cv-page-controls">
          <a-select v-model="template" size="small" class="mr-2 dropdown-select">
            <a-select-option value="" disabled>Template format</a-select-option>
            <a-select-option v-for="option in templateOptions" :key="option" :value="option">
              {{ option }}
            </a-select-option>
          </a-select>

          <a-switch
            v-if="cv || isPreviewCv"
            :checked="!!showDates"
            class="mr-2"
            checked-children="Project dates"
            un-checked-children="Project dates"
            default-unchecked
            @change="toggleDates"
          />

          <a-select
            v-model="dateFormat"
            :disabled="!showDates"
            size="small"
            class="mr-2 dropdown-select"
          >
            <a-select-option value="" disabled>Date format</a-select-option>
            <a-select-option v-for="option in dateFormatOptions" :key="option" :value="option">
              {{ moment().format(option) }}
            </a-select-option>
          </a-select>

          <a-switch
            v-if="cv || isPreviewCv"
            :checked="!!showDuration"
            class="mr-2"
            checked-children="Project duration"
            un-checked-children="Project duration"
            default-unchecked
            @change="toggleDuration"
          />

          <a-switch
            v-if="(cv || isPreviewCv) && template"
            :checked="!!showEducations"
            class="mr-2"
            checked-children="Educations"
            un-checked-children="Educations"
            @change="toggleEducations"
          />
          <a-switch
            v-if="(cv || isPreviewCv) && template"
            :checked="!!showEducationsDescription"
            class="mr-2"
            checked-children="Educations Description"
            un-checked-children="Educations Description"
            @change="toggleEducationsDescription"
          />
          <a-switch
            v-if="(cv || isPreviewCv) && template"
            :checked="!!showEducationDates"
            class="mr-2"
            checked-children="Education Dates"
            un-checked-children="Education Dates"
            @change="toggleEducationDates"
          />

          <a-switch
            v-if="cv || isPreviewCv"
            :checked="!!showSkills"
            class="mr-2"
            checked-children="Project Skills"
            un-checked-children="Project Skills"
            @change="toggleProjectSkills"
          />

          <a-switch
            v-if="cv && template !== templateOptions[Template.SKRY_V2]"
            :checked="!!showSkillCategories"
            class="mr-2"
            checked-children="Skill categories"
            un-checked-children="Skill categories"
            @change="toggleSkillCategories"
          />

          <a-switch
            v-if="cv"
            :checked="!!showEducationSkills"
            class="mr-2"
            checked-children="Education Skills"
            un-checked-children="Education Skills"
            @change="toggleEducationSkills"
          />

          <a-switch
            v-if="cv"
            :checked="!!showAreasOfExpertise"
            class="mr-2"
            checked-children="Areas of Expertise"
            un-checked-children="Areas of Expertise"
            @change="toggleAreasOfExpertise"
          />

          <a-switch
            v-if="cv || isPreviewCv"
            :checked="!!showGithub"
            class="mr-2"
            checked-children="GitHub"
            un-checked-children="GitHub"
            @change="toggleGithub"
          />

          <a-switch
            v-if="(cv || isPreviewCv) && template != 'Legacy'"
            :checked="!!showGravatar"
            class="mr-2"
            checked-children="Gravatar"
            un-checked-children="Gravatar"
            @change="toggleGravatar"
          />

          <div
            v-if="cv || isPreviewCv"
            class="mr-2"
            style="display: inline-flex; align-items: center"
          >
            <span style="margin-right: 8px">Skill Bubble Scale:</span>
            <a-slider
              v-model="skillCategoryScale"
              :min="0.5"
              :max="1"
              :step="0.1"
              :tip-formatter="formatSliderTooltip"
              style="width: 100px"
            />
          </div>

          <a-select
            v-model="languageCode"
            size="small"
            class="mr-2 dropdown-select"
            :dropdown-match-select-width="false"
            :disabled="loadingCv || Boolean(pendingNewCv)"
            @change="() => changeLanguage(languageCode)"
          >
            <a-select-option value="" disabled>Language</a-select-option>
            <a-select-option
              v-for="language in languages"
              :key="language.code"
              :value="language.code"
            >
              {{ language.name }}
              {{ language.code != LanguageCode.English ? "(AI-translated)" : "" }}
            </a-select-option>
          </a-select>

          <a-switch
            v-if="cv"
            :checked="!!isPreviewCv"
            class="mr-2"
            checked-children="Preview"
            un-checked-children="Preview"
            :disabled="languageCode !== LanguageCode.English"
            @change="togglePreview"
          />

          <a-switch
            v-if="cv && template !== templateOptions[Template.SKRY_V2]"
            :checked="!!showSkillHighlights"
            class="mr-2"
            checked-children="Skill highlighting"
            un-checked-children="Skill highlighting"
            @change="toggleSkillHighlighting"
          />

          <a-tooltip
            :title="
              languageCode === LanguageCode.English ? '' : 'Editing can only be done in English'
            "
          >
            <a-button
              v-if="cv"
              type="primary"
              class="mr-2"
              :disabled="languageCode !== LanguageCode.English"
              @click="handleEditCvVisible"
              >Edit CV</a-button
            >
          </a-tooltip>

          <a-button
            v-if="pendingNewCv && isPreviewCv && canManageCv"
            type="primary"
            :loading="loading"
            @click="handleCreateCvFromWizard(pendingNewCv)"
            >Save CV</a-button
          >
        </a-col>
      </a-row>

      <div class="cv-preview-layout">
        <div v-if="userCvs.length > 0" class="cv-switcher-row">
          <aside class="cv-switcher-sidebar">
            <h4 class="cv-switcher-title">User CVs</h4>
            <div class="cv-switcher-list">
              <button
                v-for="userCv in userCvs"
                :key="userCv.id"
                type="button"
                class="cv-switcher-item"
                :class="{ 'cv-switcher-item-active': userCv.id === activeCvId }"
                @click="handleCvSwitch(userCv.id)"
              >
                <span class="cv-switcher-item-label">{{ formatCvSwitcherLabel(userCv) }}</span>
                <span v-if="userCv.id === activeCvId" class="cv-switcher-item-actions">
                  <a-tooltip title="Copy CV">
                    <a-icon
                      type="copy"
                      class="cv-switcher-action cv-switcher-action-copy"
                      @click.stop="handleCopyCv(userCv.id)"
                    />
                  </a-tooltip>
                  <a-tooltip title="Delete CV">
                    <a-icon
                      type="delete"
                      class="cv-switcher-action cv-switcher-action-delete"
                      @click.stop="showDeleteCvConfirm(userCv.id)"
                    />
                  </a-tooltip>
                </span>
              </button>
            </div>
            <a-button
              v-if="canCreateCvForViewedUser"
              type="dashed"
              class="cv-switcher-add-btn"
              :loading="loading"
              @click="handleCreateCvForViewedUser"
              >+ Add CV</a-button
            >
          </aside>
        </div>

        <div v-else class="cv-switcher-row">
          <aside class="cv-switcher-sidebar">
            <h4 class="cv-switcher-title">User CVs</h4>
            <p class="cv-switcher-empty-text">No CVs available for this user.</p>
            <a-button
              v-if="canCreateCvForViewedUser"
              type="dashed"
              class="cv-switcher-add-btn"
              :loading="loading"
              @click="handleCreateCvForViewedUser"
              >+ Add CV</a-button
            >
          </aside>
        </div>

        <div class="cv-preview-main">
          <PreviewCvSkryV2
            v-if="
              (cv || isPreviewCv) &&
              template === templateOptions[Template.SKRY_V2] &&
              !isCvRenderLoading
            "
            id="preview-cv"
            :cv="isPreviewCv ? previewCv : cv"
            :language-code="languageCode"
            :show-dates="showDates"
            :show-duration="showDuration"
            :show-areas-of-expertise="showAreasOfExpertise"
            :show-skills="showSkills"
            :show-github="showGithub"
            :show-gravatar="showGravatar"
            :cv-background="cvBackground"
            :cv-logo="cvLogo"
            :format-project-date="formatProjectDate"
            :format-project-duration="formatProjectDuration"
            :show-educations="showEducations"
            :show-educations-description="showEducationsDescription"
            :show-education-dates="showEducationDates"
            :show-education-skills="showEducationSkills"
            :skill-category-scale="skillCategoryScale"
            :cv-options="cvOptions"
            @set-options="handleCvOptionsChange"
          />

          <a-row v-if="isCvRenderLoading" type="flex" justify="center" class="cv-render-loading">
            <a-col type="flex" align="middle" style="text-align: center">
              <a-spin>
                <a-icon slot="indicator" type="loading" style="font-size: 48px" spin />
              </a-spin>
              <p style="margin-top: 20px">
                {{
                  languageCode !== LanguageCode.English
                    ? `Generating the ${getLanguageNameFromCode(languageCode)} translation...`
                    : "Loading CV..."
                }}
              </p>
            </a-col>
          </a-row>

          <a-card v-if="!cv && !isPreviewCv && !isCvRenderLoading">
            <empty-resource-table resource="CVs" />
          </a-card>
        </div>
      </div>

      <AddCv
        :visible="addCvVisible"
        :loading="loading"
        :loading-data="loadingData"
        :cv-data="cvData"
        :can-manage-cv="canManageCv"
        :acl="baseAcl"
        @close="closeAddCvModal"
        @update-preview-cv="handleCreatePreviewCv"
        @update-cv="handleCreateCvFromWizard"
      />

      <EditCv
        v-if="cv || pendingCopyCv"
        :cv="pendingCopyCv ?? (isPreviewCv ? previewCv : cv)"
        :visible="editCvVisible"
        :loading="loading"
        :loading-data="loadingData"
        :cv-data="cvData"
        :title="pendingCopyCv ? 'Copy CV' : 'Edit CV'"
        :acl="baseAcl"
        @close="
          () => {
            editCvVisible = false;
            pendingCopyCv = undefined;
          }
        "
        @show-default-cv="handleShowDefaultCv"
        @update-preview-cv="handleUpdatePreviewCv"
        @update-cv="handleUpdateCv"
      />
    </div>
  </a-layout-content>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, onMounted, watch } from "vue";
import {
  CvInterface,
  CvDataInterface,
  MinimalCvInterface,
  MinimalWorkExperienceCvInterface,
  MinimalEducationCvInterface,
  MinimalSkillCategoryCvInterface,
  WorkExperienceCvInterface,
  SkillCvInterface,
  CvOptionsInterface,
} from "../types";
import {
  apiGetCv,
  apiUpdateCv,
  apiGetCvData,
  apiGetCvDataForUser,
  apiGetPreviewCv,
  apiCreateCv,
  apiDeleteCv,
} from "../_utils/api";
import { getCvsCompactList } from "@/api";
import { apiGetUser } from "@/modules/users/_utils/api";
import { BaseAclInterface } from "@/modules/authentication/types";
import EditCv from "../_components/EditCv.vue";
import AddCv from "../_components/AddCv.vue";
import EmptyResourceTable from "@/modules/common/_components/EmptyResourceTable.vue";
import {
  CharacteristicInterface,
  CourseInterface,
  KnowledgeDomainInterface,
} from "../../consultant_profiles/types";
import { useAuthenticationStore } from "@/modules/authentication/_store";
import { useHead } from "@unhead/vue";
import { EducationInterface, WorkExperienceInterface } from "@/modules/users/types";
import { sortByCustomSortOrder } from "@/modules/cv/_utils/sorting";
import { resolveCurrentCvId } from "@/modules/cv/_utils/currentCv";
import { formatCvDocumentTitle } from "@/modules/cv/_utils/documentTitle";
import { SkillCategoryInterface } from "@/modules/skills/types";
import PreviewCvSkryV2 from "@/modules/cv/_components/templates/PreviewCvSkryV2.vue";
import {
  changeLanguage,
  Language,
  LanguageCode,
  englishLanguage,
  swedishLanguage,
  getLanguageNameFromCode,
} from "@/i18n/index";
import { AxiosError } from "axios";
import { useDateFormatter } from "../_utils/dateFormatting";
import moment from "moment";
import { useRoute, useRouter } from "vue-router/composables";

// Pinia
const authenticationStore = useAuthenticationStore();
const baseAcl = computed<BaseAclInterface>(() => authenticationStore.baseAcl);

// Enums
enum Template {
  SKRY_V2 = 0,
}

// Data properties
const templateOptions = ref<string[]>(["Skry V2"]);
const languages = ref<Language[]>([englishLanguage, swedishLanguage]);
const loadingCv = ref<boolean>(false);
const cvData = ref<CvDataInterface | undefined>(undefined);
const previewCv = ref<CvInterface | undefined>(undefined);
const template = ref<string>(templateOptions.value[Template.SKRY_V2]);
const cv = ref<CvInterface | undefined>(undefined);
const cvLogo = ref<string | undefined>(undefined);
const editCvVisible = ref<boolean>(false);
const addCvVisible = ref<boolean>(false);
const showDuration = ref<boolean>(true);
const loadingData = ref<boolean>(false);
const showGravatar = ref<boolean>(true);
const isPreviewCv = ref<boolean>(false);
const showSkillHighlights = ref<boolean>(false);
const showGithub = ref<boolean>(false);
const cvBackground = ref<string[]>([]);
const showDates = ref<boolean>(true);
const showSkills = ref<boolean>(true);
const loading = ref<boolean>(false);
const dateFormat = ref<string>("");
const languageCode = ref<LanguageCode>(LanguageCode.English);
const showSkillCategories = ref<boolean>(true);
const showAreasOfExpertise = ref<boolean>(false);
const showEducations = ref<boolean>(true);
const showEducationsDescription = ref<boolean>(false);
const showEducationDates = ref<boolean>(false);
const showEducationSkills = ref<boolean>(false);
const skillCategoryScale = ref<number>(1);
const langCodeToPreviewCv = ref<Map<string, CvInterface>>(new Map());
const cvOptions = ref<CvOptionsInterface | undefined>({
  theme: "black",
  skillTheme: "black",
  highlighting: [],
  branding: ["skry", "skry background"],
});
const userCvs = ref<Array<MinimalCvInterface>>([]);
const switchingCvId = ref<number | undefined>(undefined);
const pendingCopyCv = ref<CvInterface | undefined>(undefined);
const pendingNewCv = ref<CvInterface | undefined>(undefined);
const viewedUser = ref<{ id: number; name: string } | undefined>(undefined);

// Instance
const instance = getCurrentInstance();
const $message = instance?.proxy.$message;
const $confirm = instance?.proxy.$confirm;
const route = useRoute();
const router = useRouter();
const { formatDate, formatProjectDate, formatProjectDuration, dateFormatOptions } =
  useDateFormatter(dateFormat);

// Computed values
const isOwnCv = computed<boolean>(() => {
  const currentUserId = authenticationStore.profile?.id;
  const cvUserId = cv.value?.user_id || cv.value?.user?.id || viewedUser.value?.id;

  return Boolean(currentUserId && cvUserId && currentUserId === cvUserId);
});

const canManageCv = computed<boolean>(() => {
  return baseAcl.value.isAdmin || baseAcl.value.isCr || baseAcl.value.isHr || isOwnCv.value;
});

const canCreateCvForViewedUser = computed<boolean>(() => {
  return canManageCv.value;
});

const viewedUserName = computed<string>(() => cv.value?.name || viewedUser.value?.name || "");

const routeCvId = computed<number | undefined>(() => {
  const cvId = parseInt(route?.params.id as string, 10);
  return Number.isNaN(cvId) ? undefined : cvId;
});
const routeUserId = computed<number | undefined>(() => {
  const userId = parseInt(route?.query?.userId as string, 10);
  return Number.isNaN(userId) ? undefined : userId;
});
const viewedUserId = computed<number | undefined>(
  () => cv.value?.user_id || cv.value?.user?.id || viewedUser.value?.id
);
const activeCvId = computed<number | undefined>(() =>
  resolveCurrentCvId(routeCvId.value, userCvs.value, cv.value?.id)
);
const isCvSwitchLoading = computed<boolean>(() => {
  return switchingCvId.value !== undefined;
});
const isCvRenderLoading = computed<boolean>(() => {
  return loadingCv.value || isCvSwitchLoading.value;
});

// Lifecycle hooks
onMounted(() => {
  // Reset any locales to default
  changeLanguage(languageCode.value);
  loadCurrentCv();
});

const title = computed<string>(() => {
  return formatCvDocumentTitle(viewedUserName.value, cv.value?.cv_alias);
});
useHead({
  title,
});

watch(languageCode, () => {
  isPreviewCv.value ? handlePreviewTranslation() : loadCurrentCv();
});

watch(
  () => `${route?.params.id || ""}:${route?.query?.userId || ""}`,
  (newRouteKey, oldRouteKey) => {
    if (!newRouteKey || newRouteKey === oldRouteKey) {
      return;
    }

    isPreviewCv.value = false;
    langCodeToPreviewCv.value = new Map<string, CvInterface>();
    loadCurrentCv();
  }
);

// Class Methods
const loadCurrentCv = (): void => {
  if (!routeCvId.value) {
    cv.value = undefined;
    previewCv.value = undefined;
    switchingCvId.value = undefined;

    if (routeUserId.value) {
      loadViewedUser(routeUserId.value);
      getCvsCompactList()
        .then((cvs) => {
          const usersCvs = cvs.filter((item) => item.uid === routeUserId.value);

          if (routeCvId.value || viewedUserId.value !== routeUserId.value) {
            return;
          }

          if (usersCvs[0]) {
            router.replace({ name: "cv", params: { id: String(usersCvs[0].id) } });
          } else {
            userCvs.value = [];
          }
        })
        .catch(() => {
          userCvs.value = [];
        });
    } else {
      userCvs.value = [];
      viewedUser.value = undefined;
    }
    return;
  }

  fetchCv(routeCvId.value);
};

const loadViewedUser = (userId: number): void => {
  if (viewedUser.value?.id === userId) {
    return;
  }

  viewedUser.value =
    authenticationStore.profile?.id === userId
      ? { id: userId, name: authenticationStore.profile.name }
      : { id: userId, name: "" };

  apiGetUser(userId)
    .then((response: any) => {
      if (viewedUser.value?.id === userId) {
        viewedUser.value = {
          id: response.data.data.id,
          name: response.data.data.name,
        };
      }
    })
    .catch(() => {
      if (viewedUser.value?.id === userId && !viewedUser.value.name) {
        $message?.error("Couldn't load user!", 3);
      }
    });
};

const fetchCv = (cvId: number): void => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  timer = setTimeout(() => {
    loadingCv.value = true;
  }, 300);

  apiGetCv(cvId, languageCode.value)
    .then((data: any) => {
      cv.value = data.data.data;
      if (cv.value) {
        cv.value.contact_email = "collaboration@skry.se";
        cv.value.contact_phone = "";
        sortAllSkills(cv.value);
        loadUserCvs(cv.value);
      }
      if (cv.value != null) updatePreviewCv(cv.value);
    })
    .catch((e: AxiosError) => {
      if (e.status === 500) {
        $message.error(
          `${getLanguageNameFromCode(languageCode.value)} translation failed: An unexpected error occurred`,
          3
        );
        languageCode.value = LanguageCode.English;
      }
    })
    .finally(() => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      loadingCv.value = false;
      switchingCvId.value = undefined;
    });
};

const loadUserCvs = (loadedCv: CvInterface): void => {
  getCvsCompactList()
    .then((cvs) => {
      const currentCv = cvs.find((item) => item.id === loadedCv.id);
      const userId = loadedCv.user_id || loadedCv.user?.id || currentCv?.uid;

      if (!userId) {
        userCvs.value = [];
        return;
      }

      viewedUser.value = {
        id: userId,
        name: loadedCv.user?.name || loadedCv.name || currentCv?.name || "",
      };
      loadViewedUser(userId);

      if (cv.value?.id === loadedCv.id) {
        userCvs.value = cvs.filter((item) => item.uid === userId);
      }
    })
    .catch(() => {
      userCvs.value = [];
    });
};

const handleCvSwitch = (cvId: number | string): void => {
  const selectedCv = Number(cvId);

  if (selectedCv === routeCvId.value) {
    return;
  }

  switchingCvId.value = selectedCv;

  router
    .push({
      name: "cv",
      params: { id: String(selectedCv) },
    })
    .catch(() => {
      switchingCvId.value = undefined;
    });
};

const handleCreateCvForViewedUser = (): void => {
  openAddCvModal();
};

const handleCopyCv = (cvId: number): void => {
  loading.value = true;
  loadingData.value = true;

  Promise.all([apiGetCv(cvId, LanguageCode.English), apiGetCvData(cvId)])
    .then(([cvRes, dataRes]: any[]) => {
      const sourceCv: CvInterface = cvRes.data.data;
      cvData.value = dataRes.data.data;

      const sourceAlias = sourceCv.cv_alias?.trim();
      pendingCopyCv.value = {
        ...sourceCv,
        cv_alias: sourceAlias ? `${sourceAlias} (Copy)` : "Copy",
      };

      editCvVisible.value = true;
    })
    .catch(() => $message?.error("Couldn't load CV for copying!", 3))
    .finally(() => {
      loading.value = false;
      loadingData.value = false;
    });
};

const handleDeleteCv = (cvId: number): void => {
  loading.value = true;
  isPreviewCv.value = false;

  apiDeleteCv(cvId)
    .then(() => {
      $message?.success("CV deleted successfully!", 3);
      const nextCv = userCvs.value.find((userCv) => userCv.id !== cvId);
      if (nextCv) {
        router.replace({ name: "cv", params: { id: String(nextCv.id) } });
      } else {
        router.replace({ name: "cvs" });
      }
    })
    .catch(() => $message?.error("Couldn't delete cv!", 3))
    .finally(() => {
      loading.value = false;
    });
};

const showDeleteCvConfirm = (cvId: number): void => {
  const mod = $confirm?.({
    title: "Are you sure you want to delete this CV?",
    okText: "Yes",
    okType: "danger",
    cancelText: "No",
    onOk() {
      handleDeleteCv(cvId);
      mod?.destroy();
    },
  });
};

const formatCvSwitcherLabel = (userCv: MinimalCvInterface): string => {
  return userCv.cv_alias ? `${userCv.cv_alias}` : `CV #${userCv.id}`;
};

const handlePreviewTranslation = (): void => {
  if (langCodeToPreviewCv.value.get(languageCode.value)) {
    previewCv.value = langCodeToPreviewCv.value.get(languageCode.value);
    sortAllSkills(previewCv.value);
    updatePreviewCv(previewCv.value);
  } else {
    fetchPreviewCv();
  }
};

const fetchPreviewCv = (): void => {
  // Initialize params to match expected format required in backend
  const previewCvParams = ref<CvInterface>({
    ...previewCv.value,
    characteristics: previewCv.value.characteristics.map(
      (characteristic: CharacteristicInterface) => characteristic.id
    ),
    courses: previewCv.value.courses.map((course: CourseInterface) => course.id),
    knowledge_domains: previewCv.value.knowledge_domains.map((kd: any) => ({
      knowledge_domain_id: kd.id ? kd.id : kd.knowledge_domain_id,
      level: kd.level,
    })),
    skill_categories: previewCv.value.skill_categories.map((cat: SkillCategoryInterface) => ({
      skill_category_id: cat.id,
    })),
    work_experiences: previewCv.value.work_experiences.map((exp: WorkExperienceCvInterface) => ({
      work_experience_id: exp.id,
      custom_role_title: exp.custom_role_title,
      custom_company_name: exp.custom_company_name,
    })),
    educations: previewCv.value.educations.map((edu: EducationInterface) => ({
      education_id: edu.id,
    })),
    skills: previewCv.value.skills.map((skill: SkillCvInterface) => ({
      ...skill,
      skill_id: skill.id ? skill.id : skill.skill_id,
      highlight: skill.highlight,
    })),
  });

  let timer: ReturnType<typeof setTimeout> | null = null;

  timer = setTimeout(() => {
    loadingCv.value = true;
  }, 300);

  apiGetPreviewCv(previewCvParams.value, languageCode.value)
    .then((data: any) => {
      previewCv.value = {
        ...data.data.data,
        contact_email: previewCv.value?.contact_email,
        contact_phone: previewCv.value?.contact_phone,
      };
      if (previewCv.value) {
        sortAllSkills(previewCv.value);
        updatePreviewCv(previewCv.value);
      }
    })
    .catch((e: AxiosError) => {
      $message.error(
        `${getLanguageNameFromCode(languageCode.value)} translation failed: An unexpected error occurred`,
        3
      );
      languageCode.value = LanguageCode.English;
    })
    .finally(() => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      loadingCv.value = false;
    });
};

const handleCvOptionsChange = (newCvOptions: CvOptionsInterface) => {
  cvOptions.value = newCvOptions;
};

const openAddCvModal = (): void => {
  pendingNewCv.value = undefined;
  addCvVisible.value = true;
  loadingData.value = true;
  if (!viewedUserId.value) {
    loadingData.value = false;
    return;
  }
  apiGetCvDataForUser(viewedUserId.value)
    .then((data) => {
      cvData.value = data;
    })
    .catch(() => {
      $message?.error("Couldn't load CV data!", 3);
    })
    .finally(() => {
      loadingData.value = false;
    });
};

const closeAddCvModal = (): void => {
  addCvVisible.value = false;
};

const handleEditCvVisible = (): void => {
  editCvVisible.value = true;
  loadingData.value = true;
  if (cv.value != null)
    apiGetCvData(cv.value.id).then((data: any) => {
      cvData.value = data.data.data;
      loadingData.value = false;
    });
};

const handleUpdateCv = (updatedCv: CvInterface): void => {
  loading.value = true;
  isPreviewCv.value = false;

  if (pendingCopyCv.value) {
    const payload: any = { ...updatedCv, user_id: viewedUserId.value };
    delete payload.id;

    apiCreateCv(payload)
      .then((data: any) => {
        const createdCv = data.data.data;
        pendingCopyCv.value = undefined;
        $message?.success("CV copied successfully!", 3);
        editCvVisible.value = false;

        if (createdCv?.id) {
          router.push({
            name: "cv",
            params: { id: String(createdCv.id) },
          });
        }
      })
      .catch(() => $message?.error("Couldn't copy cv!", 3))
      .finally(() => {
        loading.value = false;
      });
    return;
  }

  apiUpdateCv(updatedCv)
    .then((data: any) => {
      cv.value = data.data.data;
      if (cv.value) {
        cv.value.contact_email = updatedCv.contact_email;
        cv.value.contact_phone = updatedCv.contact_phone;
        cv.value.custom_logo = updatedCv.custom_logo;
        cv.value.cv_alias = updatedCv.cv_alias;
        sortAllSkills(cv.value);
        const cvIndex = userCvs.value.findIndex((uCv) => uCv.id === cv.value?.id);
        if (cvIndex !== -1 && cv.value) {
          userCvs.value[cvIndex].cv_alias = cv.value.cv_alias;
        }
      }
      if (cv.value != null) updatePreviewCv(cv.value);
      $message?.success("CV updated successfully!", 3);
      addCvVisible.value = false;
      editCvVisible.value = false;
    })
    .catch(() => $message?.error("Couldn't update cv!", 3))
    .finally(() => (loading.value = false));
};

const handleCreateCvFromWizard = (newCv: CvInterface): void => {
  loading.value = true;
  isPreviewCv.value = false;

  const payload: any = {
    ...newCv,
    user_id: viewedUserId.value,
  };
  delete payload.id;

  apiCreateCv(payload)
    .then((data: any) => {
      const createdCv = data.data.data;
      pendingNewCv.value = undefined;
      $message?.success("CV created successfully!", 3);
      addCvVisible.value = false;
      editCvVisible.value = false;

      if (createdCv?.id) {
        router.push({
          name: "cv",
          params: { id: String(createdCv.id) },
        });
      }
    })
    .catch(() => $message?.error("Couldn't create cv!", 3))
    .finally(() => {
      loading.value = false;
    });
};

const handleCreatePreviewCv = (updatedCv: CvInterface): void => {
  pendingNewCv.value = updatedCv;
  handleUpdatePreviewCv(updatedCv);
};

const handleUpdatePreviewCv = (updatedCv: CvInterface): void => {
  let updatedPreviewCv: any = { ...previewCv.value };
  updatedPreviewCv.first_title = updatedCv.first_title;
  updatedPreviewCv.second_title = updatedCv.second_title;
  updatedPreviewCv.background = updatedCv.background;
  updatedPreviewCv.contact_email = updatedCv.contact_email;
  updatedPreviewCv.contact_phone = updatedCv.contact_phone;
  updatedPreviewCv.skills = updatedCv.skills;

  langCodeToPreviewCv.value = new Map<string, CvInterface>();

  ["characteristics", "courses"].forEach((key) => {
    if (cvData.value != null)
      updatedPreviewCv[key] = cvData.value[key].filter(
        (item: CharacteristicInterface | CourseInterface) => {
          return updatedCv[key].some((id: number) => {
            return item.id === id;
          });
        }
      );
  });

  updatedPreviewCv.work_experiences = updatedCv.work_experiences.map(
    (exp: MinimalWorkExperienceCvInterface) => {
      if (cvData.value != null) {
        const experience = cvData.value.user.work_experiences.find(
          (w: WorkExperienceInterface) => w.id === exp.work_experience_id
        );
        return {
          ...experience,
          custom_role_title: exp.custom_role_title,
          featured: exp.featured,
        };
      }
    }
  );

  updatedPreviewCv.educations = updatedCv.educations.map((edu: MinimalEducationCvInterface) => {
    if (cvData.value != null) {
      const educations = cvData.value.user.educations.find(
        (e: EducationInterface) => e.id === edu.education_id
      );
      return {
        ...educations,
      };
    }
  });

  updatedPreviewCv.knowledge_domains = updatedCv.knowledge_domains.map((kdu: any) => {
    if (cvData.value != null) {
      const knowledgeDomain = cvData.value.knowledge_domains.find(
        (kd: KnowledgeDomainInterface) => kd.id === kdu.knowledge_domain_id
      );
      return {
        ...kdu,
        // @ts-ignore
        title: knowledgeDomain.title,
        // @ts-ignore
        description: knowledgeDomain[`description_${kdu.level}`],
      };
    }
  });

  updatedPreviewCv.skill_categories = updatedCv.skill_categories.map(
    (sc: MinimalSkillCategoryCvInterface) => {
      if (cvData.value != null) {
        const skillCategory = cvData.value.skill_categories.find(
          (s: SkillCategoryInterface) => s.id === sc.skill_category_id
        );
        const updatedCvSkillIds = updatedCv.skills.map((skill) => skill.id);
        // Filter the skills within the skillCategory to only include those that are in selectedSkillsIds
        const filteredSkills = skillCategory.skills.filter((skill) =>
          updatedCvSkillIds.includes(skill.id)
        );
        return {
          ...skillCategory,
          skills: filteredSkills,
        };
      }
    }
  );

  updatedPreviewCv.custom_logo = updatedCv.custom_logo;
  updatedPreviewCv.name = cv.value?.name ?? cvData.value?.user.name;

  sortAllSkills(updatedPreviewCv);
  updatePreviewCv(updatedPreviewCv);
  isPreviewCv.value = true;
  addCvVisible.value = false;
  editCvVisible.value = false;
};

const handleShowDefaultCv = (): void => {
  isPreviewCv.value = false;
  addCvVisible.value = false;
  editCvVisible.value = false;
};

const updatePreviewCv = (updatedCv: CvInterface): void => {
  previewCv.value = JSON.parse(JSON.stringify(updatedCv));
  previewCv.value && sortAllSkills(previewCv.value);
  cvBackground.value = (updatedCv.background || "").split(/\n+/g);
  cvLogo.value = updatedCv.custom_logo;

  langCodeToPreviewCv.value.set(languageCode.value, previewCv.value);
};
const toggleDates = (): void => {
  showDates.value = !showDates.value;
};

const toggleDuration = (): void => {
  showDuration.value = !showDuration.value;
};

const togglePreview = (): void => {
  isPreviewCv.value = !isPreviewCv.value;
};

const toggleSkillHighlighting = (): void => {
  showSkillHighlights.value = !showSkillHighlights.value;
};

const toggleProjectSkills = (): void => {
  showSkills.value = !showSkills.value;
};

const toggleEducationSkills = (): void => {
  showEducationSkills.value = !showEducationSkills.value;
};

const toggleSkillCategories = (): void => {
  showSkillCategories.value = !showSkillCategories.value;
};

const toggleAreasOfExpertise = (): void => {
  showAreasOfExpertise.value = !showAreasOfExpertise.value;
};

const toggleGithub = (): void => {
  showGithub.value = !showGithub.value;
};

const toggleGravatar = (): void => {
  showGravatar.value = !showGravatar.value;
};

const toggleEducations = (): void => {
  showEducations.value = !showEducations.value;
};

const toggleEducationsDescription = (): void => {
  showEducationsDescription.value = !showEducationsDescription.value;
};

const toggleEducationDates = (): void => {
  showEducationDates.value = !showEducationDates.value;
};

const formatSliderTooltip = (value: number) => `Scale: ${value}`;

// Sort skills on work experiences, projects and skill categories by the order of all skills
const sortAllSkills = (cv: CvInterface) => {
  const sortSkills = <A,>(obj: A) => ({
    ...obj,
    skills: sortByCustomSortOrder(
      obj.skills,
      cv.skills,
      (obj) => obj.id,
      (s) => s.id
    ),
  });
  cv.work_experiences = cv.work_experiences.map(sortSkills);
  cv.skill_categories = cv.skill_categories.map(sortSkills);
};
</script>

<style lang="scss">
.dropdown-select {
  min-width: 150px;
}

.cv-page-container {
  box-sizing: border-box;
  position: relative;
  width: 100%;
  max-width: none;
  min-width: 0;
  margin: 0;
  padding: 0;
}

.cv-page-content {
  margin: 24px 0 !important;
}

.cv-page-header {
  flex-wrap: wrap;
  gap: 8px;
}

.cv-page-title {
  flex: 0 1 auto;
}

.cv-page-controls {
  display: flex;
  flex: 1 1 500px;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.cv-page-controls .mr-2 {
  margin-right: 0 !important;
}

.cv-preview-layout {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: calc(186px + 274mm);
}

.cv-preview-main {
  flex: 0 0 auto;
}

.cv-switcher-row {
  position: static;
  flex: 0 0 170px;
  margin: 0;
}

.cv-switcher-sidebar {
  width: 170px;
  max-width: 100%;
  max-height: none;
  margin-top: 0;
  margin-left: 0;
  z-index: 31;
  pointer-events: auto;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
  padding: 10px;
  display: flex;
  flex-direction: column;
}

.cv-switcher-title {
  margin: 0 0 8px;
  font-size: 14px;
}

.cv-switcher-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cv-switcher-item {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
  text-align: left;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.cv-switcher-item:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}

.cv-switcher-item-active {
  border-color: #1890ff;
  background: #e6f7ff;
}

.cv-switcher-item-label {
  font-size: 13px;
  color: #1f1f1f;
}

.cv-switcher-item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cv-switcher-action {
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.cv-switcher-action:hover {
  opacity: 0.75;
}

.cv-switcher-item-actions .cv-switcher-action-copy,
.cv-switcher-item-actions .cv-switcher-action-copy.anticon {
  color: #1890ff !important;
}

.cv-switcher-item-actions .cv-switcher-action-delete,
.cv-switcher-item-actions .cv-switcher-action-delete.anticon {
  color: #ff4d4f !important;
}

.cv-render-loading {
  margin-top: 100px;
}

.cv-switcher-add-btn {
  margin-top: 10px;
  width: 100%;
}

.cv-switcher-empty-text {
  margin: 4px 0 8px;
  font-size: 12px;
  color: #595959;
}

@media (max-width: 767px) {
  .cv-preview-layout {
    display: block;
    min-width: 0;
  }

  .cv-switcher-row {
    margin-bottom: 16px;
  }

  .cv-switcher-sidebar {
    width: 100%;
  }
}

@media not print {
  .scroll-x {
    min-width: 0;
    overflow-x: auto;
  }
}

@media print {
  html,
  body,
  #app {
    height: auto !important;
  }

  #app > .ant-layout,
  #app > .ant-layout > .ant-layout {
    display: block !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .ant-layout-header {
    display: none !important;
  }

  .cv-page-content {
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
  }

  .cv-page-container {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .container {
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
  }

  .container > *:not(.cv-preview-layout),
  .cv-switcher-row,
  .cv-preview-main > :not(#preview-cv) {
    display: none !important;
  }

  .cv-preview-layout,
  .cv-preview-main,
  #preview-cv {
    display: block !important;
  }

  .cv-preview-layout {
    min-width: 0;
  }

  .sidebar-wrapper,
  .sidebar-filler {
    display: none !important;
  }
}
</style>
